/**
 * Les generateurs de mathematiques: calcul, geometrie et mesure, problemes.
 *
 * Chaque generateur est une fonction pure d'un `Alea`. La difficulte ne se
 * decide pas ici: elle vient de la place de l'activite dans la table de
 * progression (progression.ts). Ici on ne choisit que les NOMBRES, et on les
 * choisit dans des bornes qui appartiennent au niveau.
 */
import type { Alea } from '../alea.ts'
import type { Brouillon } from './atelier.ts'
import { composerChoix, tailleDe } from './atelier.ts'
import { compte, deNom, NOMS_COMPTABLES, PRENOMS } from './lexique.ts'
import type { Nom, Prenom } from './lexique.ts'
import { formaterNombre } from './notation.ts'

/** Une question de calcul pose toujours la meme consigne. */
function calcul(expression: string, valeur: number): Brouillon {
  return {
    enonce: `Combien font ${expression} ?`,
    consigne: 'Écris le résultat.',
    saisie: 'nombre',
    reponses: [formaterNombre(valeur)],
    taille: tailleDe(valeur),
  }
}

/** Comment une unite se dit dans une consigne: « en cm² » se lit mal a voix haute. */
const NOM_UNITE: Record<string, string> = {
  cm: 'centimètres',
  m: 'mètres',
  g: 'grammes',
  'cm²': 'centimètres carrés',
  'cm³': 'centimètres cubes',
  '°': 'degrés',
  $: 'dollars',
}

/** Une reponse chiffree avec son unite. */
function mesure(enonce: string, valeur: number, unite: string, taille: number): Brouillon {
  return {
    enonce,
    consigne: `Écris le résultat en ${NOM_UNITE[unite] ?? unite}.`,
    saisie: 'nombre',
    reponses: [formaterNombre(valeur)],
    unite,
    taille,
  }
}

/** Une somme d'argent a la quebecoise: deux decimales, virgule, dollar apres. */
function argent(cents: number): string {
  return (cents / 100).toFixed(2).replace('.', ',')
}

// ================================================================ arithmetique

export function addition(min: number, max: number, plafond: number): (a: Alea) => Brouillon {
  return (a) => {
    const x = a.entier(min, max)
    const y = a.entier(min, Math.max(min, plafond - x))
    return calcul(`${x} + ${y}`, x + y)
  }
}

export function soustraction(minA: number, maxA: number, minB: number): (a: Alea) => Brouillon {
  return (a) => {
    const x = a.entier(minA, maxA)
    const y = a.entier(minB, Math.max(minB, x - 1))
    return calcul(`${x} − ${y}`, x - y)
  }
}

export function multiplication(min: number, max: number, minB: number, maxB: number): (a: Alea) => Brouillon {
  return (a) => {
    const x = a.entier(min, max)
    const y = a.entier(minB, maxB)
    return calcul(`${x} × ${y}`, x * y)
  }
}

/** Division toujours EXACTE: on part du quotient, jamais du dividende. */
export function division(minQ: number, maxQ: number, minD: number, maxD: number): (a: Alea) => Brouillon {
  return (a) => {
    const q = a.entier(minQ, maxQ)
    const d = a.entier(minD, maxD)
    return calcul(`${q * d} ÷ ${d}`, q)
  }
}

/** Somme de deux fractions de meme denominateur. La reponse reste une fraction. */
export function fractionSomme(a: Alea): Brouillon {
  const d = a.entier(4, 10)
  const x = a.entier(1, d - 2)
  const y = a.entier(1, d - 1 - x)
  return {
    enonce: `Combien font ${x}/${d} + ${y}/${d} ?`,
    consigne: 'Écris la fraction, par exemple 3/4.',
    saisie: 'nombre',
    reponses: [`${x + y}/${d}`],
    taille: d - 4,
  }
}

/** Une fraction d'un nombre entier, choisie pour tomber juste. */
export function fractionDe(a: Alea): Brouillon {
  const d = a.entier(2, 5)
  const k = a.entier(1, d - 1)
  const m = a.entier(4, 20)
  return calcul(`${k}/${d} de ${d * m}`, k * m)
}

/** Addition de deux nombres a une decimale. Le calcul se fait en dixiemes. */
export function decimalSomme(a: Alea): Brouillon {
  const x = a.entier(11, 99)
  const y = a.entier(11, 99)
  return calcul(`${formaterNombre(x / 10, 1)} + ${formaterNombre(y / 10, 1)}`, (x + y) / 10)
}

/** Multiplication d'un decimal par un entier. */
export function decimalMultiplication(a: Alea): Brouillon {
  const x = a.entier(11, 99)
  const f = a.choix([2, 3, 4, 5, 10])
  return calcul(`${formaterNombre(x / 10, 1)} × ${f}`, (x * f) / 10)
}

const POURCENTAGES: readonly number[] = [10, 20, 25, 50, 75]

export function pourcentage(a: Alea): Brouillon {
  const p = a.choix(POURCENTAGES)
  const n = 20 * a.entier(2, 10)
  return calcul(`${p} % de ${n}`, (p * n) / 100)
}

/** Fractions deja reduites: on les multiplie, l'enfant doit revenir en arriere. */
const FRACTIONS_REDUITES: readonly (readonly number[])[] = [
  [1, 2],
  [1, 3],
  [2, 3],
  [1, 4],
  [3, 4],
  [1, 5],
  [2, 5],
  [3, 5],
  [4, 5],
  [1, 6],
  [5, 6],
  [3, 8],
  [5, 8],
]

export function fractionSimplifier(a: Alea): Brouillon {
  const base = a.choix(FRACTIONS_REDUITES)
  const p = base[0] ?? 1
  const q = base[1] ?? 2
  const k = a.entier(2, 5)
  const bon = `${p}/${q}`
  return {
    enonce: `Simplifie la fraction ${p * k}/${q * k}.`,
    consigne: 'Choisis la fraction la plus simple.',
    saisie: 'choix',
    reponses: [bon],
    choix: composerChoix(a, bon, [`${p * k}/${q * k}`, `${q}/${p}`, `${p + 1}/${q + 1}`, `${p}/${q * k}`]),
    taille: q,
  }
}

// ================================================================== geometrie

interface Forme {
  nom: string
  cotes: number
}

/** Formes dont le nombre de cotes est sans ambiguite (pas de rectangle ici). */
const FORMES: readonly Forme[] = [
  { nom: 'triangle', cotes: 3 },
  { nom: 'carré', cotes: 4 },
  { nom: 'pentagone', cotes: 5 },
  { nom: 'hexagone', cotes: 6 },
]

export function compterCotes(a: Alea): Brouillon {
  const f = a.choix(FORMES)
  return {
    enonce: `Combien de côtés a un ${f.nom} ?`,
    consigne: 'Écris le nombre de côtés.',
    saisie: 'nombre',
    reponses: [String(f.cotes)],
    taille: f.cotes - 3,
  }
}

export function nommerForme(a: Alea): Brouillon {
  const f = a.choix(FORMES)
  const autres = FORMES.filter((x) => x.nom !== f.nom).map((x) => x.nom)
  return {
    enonce: `Quelle forme a exactement ${f.cotes} côtés ?`,
    consigne: 'Choisis la bonne forme.',
    saisie: 'choix',
    reponses: [f.nom],
    choix: composerChoix(a, f.nom, [...autres, 'cercle']),
    taille: f.cotes - 3,
  }
}

export function perimetreCarre(a: Alea): Brouillon {
  const c = a.entier(3, 12)
  return mesure(`Un carré a des côtés de ${c} cm. Quel est son périmètre ?`, 4 * c, 'cm', tailleDe(4 * c))
}

export function perimetreRectangle(a: Alea): Brouillon {
  const grand = a.entier(6, 18)
  // La LONGUEUR est le grand cote: « 6 cm de long et 7 cm de large » est
  // sorti au telephone le 13 octobre. Toujours un tirage, seule la borne change.
  const petit = a.entier(2, Math.min(9, grand - 1))
  return mesure(
    `Un rectangle mesure ${grand} cm de long et ${petit} cm de large. Quel est son périmètre ?`,
    2 * (grand + petit),
    'cm',
    tailleDe(2 * (grand + petit)),
  )
}

/**
 * Une conversion decrite par sa table: 1 grande unite vaut `facteur` petites.
 * `symbole` est ce qu'on affiche a cote de la reponse, `petite` ce qu'on lit.
 */
export function conversion(
  grande: string,
  petite: string,
  symbole: string,
  facteur: number,
): (a: Alea) => Brouillon {
  return (a) => {
    const n = a.entier(2, 9)
    return mesure(`Combien y a-t-il de ${petite} dans ${n} ${grande} ?`, n * facteur, symbole, tailleDe(n * facteur))
  }
}

export function aireRectangle(a: Alea): Brouillon {
  const grand = a.entier(4, 15)
  const petit = a.entier(3, 9)
  return mesure(
    `Un rectangle mesure ${grand} cm sur ${petit} cm. Quelle est son aire ?`,
    grand * petit,
    'cm²',
    tailleDe(grand * petit),
  )
}

export function aireCarre(a: Alea): Brouillon {
  const c = a.entier(3, 12)
  return mesure(`Quelle est l’aire d’un carré de ${c} cm de côté ?`, c * c, 'cm²', tailleDe(c * c))
}

export function aireTriangle(a: Alea): Brouillon {
  const base = 2 * a.entier(2, 10)
  const hauteur = a.entier(3, 12)
  return mesure(
    `Un triangle a une base de ${base} cm et une hauteur de ${hauteur} cm. Quelle est son aire ?`,
    (base * hauteur) / 2,
    'cm²',
    tailleDe((base * hauteur) / 2),
  )
}

export function angleManquant(a: Alea): Brouillon {
  const un = a.entier(20, 90)
  const deux = a.entier(20, 160 - un)
  return mesure(
    `Dans un triangle, deux angles mesurent ${un}° et ${deux}°. Combien mesure le troisième ?`,
    180 - un - deux,
    '°',
    tailleDe(180 - un - deux),
  )
}

export function typeAngle(a: Alea): Brouillon {
  const angle = a.chance(0.2) ? 90 : a.chance(0.5) ? a.entier(10, 89) : a.entier(91, 170)
  const bon = angle === 90 ? 'droit' : angle < 90 ? 'aigu' : 'obtus'
  return {
    enonce: `Un angle mesure ${angle}°. Est-il aigu, droit ou obtus ?`,
    consigne: 'Choisis la bonne réponse.',
    saisie: 'choix',
    reponses: [bon],
    choix: composerChoix(a, bon, ['aigu', 'droit', 'obtus'], 3),
    taille: 3,
  }
}

/** Pi, arrondi comme on l'apprend a l'ecole primaire. */
const PI = 3.14

export function circonference(a: Alea): Brouillon {
  const r = a.entier(2, 10)
  return mesure(
    `On prend π = 3,14. Quelle est la circonférence d’un cercle de rayon ${r} cm ?`,
    2 * PI * r,
    'cm',
    tailleDe(2 * PI * r),
  )
}

export function aireDisque(a: Alea): Brouillon {
  const r = a.entier(2, 10)
  return mesure(
    `On prend π = 3,14. Quelle est l’aire d’un disque de rayon ${r} cm ?`,
    PI * r * r,
    'cm²',
    tailleDe(PI * r * r),
  )
}

export function volumePave(a: Alea): Brouillon {
  const x = a.entier(2, 9)
  const y = a.entier(2, 9)
  const z = a.entier(2, 9)
  return mesure(
    `Un pavé droit mesure ${x} cm sur ${y} cm sur ${z} cm. Quel est son volume ?`,
    x * y * z,
    'cm³',
    tailleDe(x * y * z),
  )
}

// ================================================================== problemes

/** « il » ou « elle », selon le prenom: une histoire ne se trompe pas de genre. */
function pronom(p: Prenom): string {
  return p.genre === 'f' ? 'elle' : 'il'
}

function sujet(p: Prenom): string {
  return p.genre === 'f' ? 'Elle' : 'Il'
}

function tirerNom(a: Alea): Nom {
  return a.choix(NOMS_COMPTABLES)
}

/** On en donne encore: une addition racontee. */
export function problemeTotal(minX: number, maxX: number, minY: number, maxY: number): (a: Alea) => Brouillon {
  return (a) => {
    const qui = a.choix(PRENOMS)
    const quoi = tirerNom(a)
    const x = a.entier(minX, maxX)
    const y = a.entier(minY, maxY)
    return {
      enonce: `${qui.nom} a ${compte(x, quoi)}. On lui en donne ${y} de plus. Combien en a-t-${pronom(qui)} en tout ?`,
      consigne: 'Écris le nombre.',
      saisie: 'nombre',
      reponses: [String(x + y)],
      taille: tailleDe(x + y),
    }
  }
}

/** On en perd: une soustraction racontee. */
export function problemeReste(minX: number, maxX: number, minY: number): (a: Alea) => Brouillon {
  return (a) => {
    const qui = a.choix(PRENOMS)
    const quoi = tirerNom(a)
    const x = a.entier(minX, maxX)
    const y = a.entier(minY, Math.max(minY, x - 1))
    return {
      enonce: `${qui.nom} avait ${compte(x, quoi)}. ${sujet(qui)} en a perdu ${y}. Combien lui en reste-t-il ?`,
      consigne: 'Écris le nombre.',
      saisie: 'nombre',
      reponses: [String(x - y)],
      taille: tailleDe(x - y),
    }
  }
}

/** Des boites identiques: une multiplication racontee. */
export function problemePaquets(minB: number, maxB: number, minP: number, maxP: number): (a: Alea) => Brouillon {
  return (a) => {
    const quoi = tirerNom(a)
    const boites = a.entier(minB, maxB)
    const parBoite = a.entier(minP, maxP)
    return {
      enonce: `Il y a ${boites} boîtes de ${parBoite} ${quoi.p}. Combien y a-t-il ${deNom(quoi)} en tout ?`,
      consigne: 'Écris le nombre.',
      saisie: 'nombre',
      reponses: [String(boites * parBoite)],
      taille: tailleDe(boites * parBoite),
    }
  }
}

/** Un partage egal: une division racontee, toujours exacte. */
export function problemePartage(minA: number, maxA: number, minQ: number, maxQ: number): (a: Alea) => Brouillon {
  return (a) => {
    const quoi = tirerNom(a)
    const amis = a.entier(minA, maxA)
    const chacun = a.entier(minQ, maxQ)
    return {
      enonce: `On partage ${amis * chacun} ${quoi.p} entre ${amis} amis, en parts égales. Combien chacun en reçoit-il ?`,
      consigne: 'Écris le nombre.',
      saisie: 'nombre',
      reponses: [String(chacun)],
      taille: tailleDe(chacun),
    }
  }
}

/** De l'argent: des decimaux qu'on rencontre pour vrai. */
export function problemeArgent(a: Alea): Brouillon {
  const qui = a.choix(PRENOMS)
  const un = a.entier(150, 2500)
  const deux = a.entier(75, 1200)
  return {
    enonce: `${qui.nom} achète un livre à ${argent(un)} $ et un crayon à ${argent(deux)} $. Combien paie-t-${pronom(qui)} en tout ?`,
    consigne: 'Écris le montant en dollars.',
    saisie: 'nombre',
    reponses: [argent(un + deux)],
    unite: '$',
    taille: 6,
  }
}

/** Une longueur decimale qu'on raccourcit. */
export function problemeLongueur(a: Alea): Brouillon {
  const total = a.entier(40, 99)
  const coupe = a.entier(5, total - 5)
  return {
    enonce: `Une planche mesure ${formaterNombre(total / 10, 1)} m. On en coupe ${formaterNombre(coupe / 10, 1)} m. Combien reste-t-il de mètres ?`,
    consigne: 'Écris le résultat en mètres.',
    saisie: 'nombre',
    reponses: [formaterNombre((total - coupe) / 10, 1)],
    unite: 'm',
    taille: 5,
  }
}

/** Un rabais: le pourcentage applique a quelque chose de concret. */
export function problemeRabais(a: Alea): Brouillon {
  const prix = 20 * a.entier(2, 10)
  const p = a.choix([10, 20, 25, 50])
  return {
    enonce: `Un chandail coûte ${prix} $. Il est réduit de ${p} %. Combien coûte-t-il maintenant ?`,
    consigne: 'Écris le nouveau prix en dollars.',
    saisie: 'nombre',
    reponses: [formaterNombre(prix - (prix * p) / 100)],
    unite: '$',
    taille: 7,
  }
}

/** Une vitesse moyenne: une division a deux etages. */
export function problemeVitesse(a: Alea): Brouillon {
  const heures = a.entier(2, 6)
  const vitesse = 5 * a.entier(8, 18)
  return {
    enonce: `Un autobus parcourt ${heures * vitesse} km en ${heures} heures. Quelle est sa vitesse moyenne ?`,
    consigne: 'Écris la vitesse en kilomètres à l’heure.',
    saisie: 'nombre',
    reponses: [String(vitesse)],
    unite: 'km/h',
    taille: 8,
  }
}
